import { useEffect } from "react";

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#6B5D52";
const BORDER = "#E4D9CC";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

export default function ContractClientPage({ projectData, setProjectData }) {
  const {
    nomClient,
    nomProjet,
    adresseProjet,
    montantGlobal,
    dateContrat,
    contratSigne,
  } = projectData;

  useEffect(() => {
    const id = "yoma-fonts-contract-client";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700&display=swap";
    document.head.appendChild(link);
  }, []);

  function updateField(field, value) {
    setProjectData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }

  const cabinetName =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("yoma_company_name")) ||
    "MON CABINET";

  function formatDateDisplay(iso) {
    if (!iso) return "—";
    try {
      return new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  }

  const signed = Boolean(contratSigne);

  const labelStyle = {
    fontFamily: FF_DM,
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: MUTED,
    marginBottom: 8,
  };

  const inputBase = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: `1px solid ${BORDER}`,
    boxSizing: "border-box",
    fontFamily: FF_DM,
    fontSize: 15,
    color: TAUPE,
    background: BG_CARD,
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  };

  const row = (label, value, mono) => (
    <div
      style={{
        paddingBottom: 14,
        marginBottom: 14,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div style={labelStyle}>{label}</div>
      <div
        style={{
          fontFamily: mono ? FF_PLAYFAIR : FF_DM,
          fontSize: mono ? 22 : 15,
          fontWeight: mono ? 600 : 500,
          color: mono ? GOLD : TAUPE,
          lineHeight: 1.4,
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .contract-client-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(300px, 0.9fr);
          gap: 28px;
          align-items: start;
        }
        @media (max-width: 960px) {
          .contract-client-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <main
        style={{
          padding: "8px 0 40px",
          margin: 0,
          minHeight: "100%",
          background: BG,
          fontFamily: FF_DM,
          color: TAUPE,
        }}
      >
        {/* HEADER */}
        <header style={{ marginBottom: 36 }}>
          <div
            style={{
              fontFamily: FF_DM,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 10,
            }}
          >
            {cabinetName}
          </div>
          <h1
            style={{
              fontFamily: FF_PLAYFAIR,
              fontSize: 32,
              fontWeight: 600,
              color: TAUPE,
              margin: "0 0 10px",
              lineHeight: 1.15,
            }}
          >
            Contrat Client
          </h1>
          <p
            style={{
              fontFamily: FF_DM,
              fontSize: 15,
              color: MUTED,
              margin: 0,
              maxWidth: 520,
              lineHeight: 1.55,
            }}
          >
            Base contractuelle du projet YOMA
          </p>
        </header>

        <div className="contract-client-grid">
          {/* COLONNE GAUCHE — Formulaire */}
          <div
            style={{
              background: BG_CARD,
              borderRadius: 16,
              border: `1px solid ${BORDER}`,
              boxShadow: "0 12px 40px rgba(61, 46, 32, 0.06)",
              padding: "28px 26px 30px",
            }}
          >
            <h2
              style={{
                fontFamily: FF_PLAYFAIR,
                fontSize: 22,
                fontWeight: 600,
                color: TAUPE,
                margin: "0 0 22px",
              }}
            >
              Informations principales
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div>
                <div style={labelStyle}>Nom du client</div>
                <input
                  style={inputBase}
                  value={nomClient}
                  onChange={(e) => updateField("nomClient", e.target.value)}
                />
              </div>
              <div>
                <div style={labelStyle}>Nom du projet</div>
                <input
                  style={inputBase}
                  value={nomProjet}
                  onChange={(e) => updateField("nomProjet", e.target.value)}
                />
              </div>
              <div>
                <div style={labelStyle}>Adresse / Référence du projet</div>
                <input
                  style={inputBase}
                  value={adresseProjet}
                  onChange={(e) => updateField("adresseProjet", e.target.value)}
                />
              </div>

              {/* Montant TTC — mis en avant */}
              <div
                style={{
                  padding: "18px 18px 20px",
                  borderRadius: 14,
                  border: `2px solid ${GOLD}`,
                  background: "linear-gradient(145deg, rgba(192,154,91,0.12) 0%, rgba(248,244,238,0.95) 100%)",
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.8), 0 4px 20px rgba(192,154,91,0.15)`,
                }}
              >
                <div
                  style={{
                    ...labelStyle,
                    color: TAUPE,
                    marginBottom: 10,
                  }}
                >
                  Montant global TTC (MAD)
                </div>
                <input
                  style={{
                    ...inputBase,
                    fontFamily: FF_PLAYFAIR,
                    fontSize: 28,
                    fontWeight: 600,
                    color: GOLD,
                    border: `1px solid rgba(192,154,91,0.45)`,
                    background: BG_CARD,
                    padding: "14px 16px",
                  }}
                  value={montantGlobal}
                  onChange={(e) => updateField("montantGlobal", e.target.value)}
                  inputMode="decimal"
                />
              </div>

              <div>
                <div style={labelStyle}>Date du contrat</div>
                <input
                  type="date"
                  style={inputBase}
                  value={dateContrat}
                  onChange={(e) => updateField("dateContrat", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* COLONNE DROITE — Synthèse live */}
          <div
            style={{
              background: BG_CARD,
              borderRadius: 16,
              border: `2px solid ${GOLD}`,
              boxShadow: "0 16px 48px rgba(192, 154, 91, 0.12)",
              padding: "28px 24px 26px",
              position: "sticky",
              top: 16,
            }}
          >
            <h2
              style={{
                fontFamily: FF_PLAYFAIR,
                fontSize: 24,
                fontWeight: 600,
                color: TAUPE,
                margin: "0 0 8px",
              }}
            >
              Synthèse contrat
            </h2>

            <div style={{ marginBottom: 18 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: FF_DM,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  padding: "8px 14px",
                  borderRadius: 999,
                  background: signed ? "rgba(192,154,91,0.18)" : "rgba(61,46,32,0.08)",
                  color: signed ? GOLD : MUTED,
                  border: `1px solid ${signed ? "rgba(192,154,91,0.45)" : BORDER}`,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: signed ? GOLD : MUTED,
                    opacity: signed ? 1 : 0.5,
                  }}
                />
                {signed ? "Contrat signé" : "En attente de signature"}
              </span>
            </div>

            <div style={{ marginBottom: 8 }}>
              {row("Client", nomClient)}
              {row("Projet", nomProjet)}
              {row("Adresse", adresseProjet)}
              {row("Montant TTC", formatMoney(montantGlobal), true)}
              <div style={{ paddingBottom: 0, marginBottom: 0, borderBottom: "none" }}>
                <div style={labelStyle}>Date</div>
                <div
                  style={{
                    fontFamily: FF_DM,
                    fontSize: 15,
                    fontWeight: 500,
                    color: TAUPE,
                  }}
                >
                  {formatDateDisplay(dateContrat)}
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 22,
                paddingTop: 20,
                borderTop: `1px solid ${BORDER}`,
                fontFamily: FF_DM,
                fontSize: 13,
                lineHeight: 1.75,
                color: MUTED,
              }}
            >
              Le client voit le montant global,
              <br />
              pas les enveloppes internes.
              <br />
              Le mandat financier fait partie du modèle YOMA.
              <br />
              Aucun démarrage avant signature et encaissement.
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
